// src/services/custom-oauth.service.js

import EventEmitter from 'events';
import { useAuthStore } from '../stores/auth';
import { generateRandomState, generateRandomNonce } from '../util/oauth-utils';
import { createEncodedState } from '../util/state-utils';

const CLIENT_ID = import.meta.env.VITE_CUSTOM_OAUTH_CLIENT_ID;
const OAUTH_BASE_URL = import.meta.env.VITE_CUSTOM_OAUTH_BASE_URL;
const REDIRECT_URI = window.location.protocol + '//' + window.location.host + '/callback';
const localStorageKey = 'customOAuthLoggedIn';
const loginEvent = 'loginEvent';

class CustomOAuthService extends EventEmitter {
  idToken = null;
  tokenExpiry = null;
  _accessToken = null;
  _refreshToken = null;
  _refreshRequest = null;
  profile = null;

  // Starts the user login flow
  login(options = {}) {
    // Handle both old string state format and new options object format
    let state = generateRandomState();

    if (options && typeof options.state === 'string') {
      state = options.state;
    } else if (options && typeof options === 'object' && Object.keys(options).length > 0) {
      // Convert options object using state utils
      state = createEncodedState(options);
    }

    const nonce = generateRandomNonce();

    // Build the authorize URL with required parameters
    let authUrl = `${OAUTH_BASE_URL}/api/oauth/authorize?client_id=${CLIENT_ID}&redirect_uri=${encodeURIComponent(
      REDIRECT_URI
    )}&response_type=code&scope=openid profile email&state=${encodeURIComponent(state)}&nonce=${encodeURIComponent(nonce)}&identity_provider=emailotp`;

    // Add email parameter if provided in options
    if (options) {
      if (options.email) authUrl += `&email=${encodeURIComponent(options.email)}`;
    }

    window.location.href = authUrl;
  }

  // Handles the callback request from custom OAuth server
  async parseCallbackAccessToken() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (error) {
      const fullError = errorDescription ? `${error}: ${errorDescription}` : error;
      // eslint-disable-next-line no-console

      throw new Error(`OAuth authentication failed: ${fullError}`);
    }

    if (!code) {
      const availableParams = Array.from(urlParams.keys());
      // Check if the URL has fragment parameters instead of query parameters
      const urlFragment = window.location.hash;
      if (urlFragment && urlFragment.includes('code=')) {
        // eslint-disable-next-line no-console
        const fragmentParams = new URLSearchParams(urlFragment.substring(1));
        const fragmentCode = fragmentParams.get('code');
        if (fragmentCode) {
          // Use the code from fragment and continue with normal flow
          try {
            const tokenResponse = await this.exchangeCodeForTokens(fragmentCode);
            if (tokenResponse && tokenResponse.access_token) {
              await this.localLogin(tokenResponse, state);
              return tokenResponse.access_token;
            }
            throw new Error('Invalid token response');
          } catch (fragmentError) {
            throw new Error(`Token exchange failed with fragment code: ${fragmentError.message}`);
          }
        }
      }

      // Check if we're in an invalid state and should redirect to login
      const currentPath = window.location.pathname;
      if (currentPath === '/callback' && availableParams.length === 0) {
        // eslint-disable-next-line no-console
        const error = new Error('OAuth flow was interrupted. Please try logging in again.');
        error.type = 'OAUTH_FLOW_INTERRUPTED';
        throw error;
      }

      throw new Error(`No authorization code found in callback URL.`);
    }

    try {
      // Exchange code for tokens
      const tokenResponse = await this.exchangeCodeForTokens(code);

      if (tokenResponse && tokenResponse.access_token) {
        await this.localLogin(tokenResponse, state);

        return tokenResponse.access_token;
      }

      throw new Error('Invalid token response');
    } catch (error) {
      throw new Error(`Token exchange failed: ${error.message}`);
    }
  }

  // Exchange authorization code for access token via backend
  async exchangeCodeForTokens(code) {
    // Check if code is null or undefined
    if (!code) {
      // eslint-disable-next-line no-console

      throw new Error('Cannot exchange tokens: authorization code is null or undefined');
    }

    // Use the same base URL pattern as other API calls
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    const backendTokenUrl = `${baseUrl}/api/oauth/token-exchange?journey=Token%20Exchange`;

    const response = await fetch(backendTokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        code: code,
        redirect_uri: REDIRECT_URI
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorDescription = errorData.error_description || 'Unknown error';

      // Show friendly error page for internal server errors
      if (errorDescription.toLowerCase().includes('internal server error')) {
        const friendlyError = new Error('Authentication Service Temporarily Unavailable');
        friendlyError.type = 'OAUTH_SERVER_ERROR';
        friendlyError.friendlyMessage =
          "We're experiencing technical difficulties with our login system. Please try again in a few minutes.";
        throw friendlyError;
      }

      throw new Error(`Token exchange failed: ${response.status} ${response.statusText} - ${errorDescription}`);
    }

    return await response.json();
  }

  // Exchange an ASURITE (Cognito) ID token for aether tokens via the api-hub's
  // backend-to-backend grant. Mirrors exchangeCodeForTokens but seeds the exchange
  // with the Cognito ID token instead of an authorization code — no aether redirect,
  // session, or cross-site cookie.
  async exchangeAsuriteTokenForTokens(idToken) {
    if (!idToken) {
      throw new Error('Cannot exchange tokens: ASURITE ID token is null or undefined');
    }

    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    const url = `${baseUrl}/api/oauth/asurite-exchange?journey=Token%20Exchange`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ id_token: idToken })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const err = new Error(errorData.error_description || `ASURITE token exchange failed: ${response.status}`);
      // aether returns code:'account_deactivated' for a deactivated user.
      err.type = errorData.code || errorData.error || 'ASURITE_EXCHANGE_FAILED';
      throw err;
    }

    return await response.json();
  }

  // Get user profile information
  async getUserProfile(accessToken, { redirectOnUnauthorized = true } = {}) {
    const profileUrl = `${OAUTH_BASE_URL}/api/oidc/userinfo`;

    const response = await fetch(profileUrl, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Clear localStorage tokens and redirect to main login
        this.clearLocalStorage();
        this.idToken = null;
        this.tokenExpiry = null;
        this._accessToken = null;
        this._refreshToken = null;
        this.profile = null;

        // Update Pinia store
        const authStore = useAuthStore();
        authStore.setAuth(false);

        // ULC-3226: probes (the connector's session check) suppress this redirect —
        // for them a dead token means "fall through to the ASURITE exchange", not
        // "bounce the learner to the OTP login page".
        if (redirectOnUnauthorized) {
          setTimeout(() => this.login(), 200);
        }

        throw new Error('Unauthorized: Token invalid, redirecting to login');
      }
      throw new Error(`Profile fetch failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  async localLogin(tokenResponse, state = null, { emitEvent = true } = {}) {
    this._accessToken = tokenResponse.access_token;
    this._refreshToken = tokenResponse.refresh_token;
    this.idToken = tokenResponse.id_token;

    // Use user profile from token response if available, otherwise fetch it
    if (tokenResponse.user_info && Object.keys(tokenResponse.user_info).length > 0) {
      this.profile = tokenResponse.user_info;
    } else {
      try {
        this.profile = await this.getUserProfile(this._accessToken);
      } catch (error) {
        // Failed to fetch user profile, continue with empty profile
        this.profile = {};
      }
    }

    // Set token expiry (default to 1 hour if not provided)
    const expiresIn = tokenResponse.expires_in || 3600;
    this.tokenExpiry = new Date(Date.now() + expiresIn * 1000);

    localStorage.setItem(localStorageKey, 'true');
    localStorage.setItem(`${localStorageKey}_token`, this._accessToken);
    localStorage.setItem(`${localStorageKey}_expiry`, this.tokenExpiry.getTime().toString());

    // Store refresh token if provided
    if (this._refreshToken) {
      localStorage.setItem(`${localStorageKey}_refresh`, this._refreshToken);
    }

    if (emitEvent) {
      this.emit(loginEvent, {
        loggedIn: true,
        tokenResponse,
        state: state || {}
      });
    }

    // Update Pinia store
    const authStore = useAuthStore();
    authStore.setAuth(true);
  }

  // Silent authentication check - restores tokens from localStorage or refreshes if expired
  async silentAuth({ redirectOnUnauthorized = true } = {}) {
    const savedToken = localStorage.getItem(`${localStorageKey}_token`);
    const savedExpiry = localStorage.getItem(`${localStorageKey}_expiry`);
    const savedRefresh = localStorage.getItem(`${localStorageKey}_refresh`);

    if (savedToken && savedExpiry) {
      const expiryTime = new Date(parseInt(savedExpiry));

      if (expiryTime > new Date()) {
        // Access token still valid - restore it
        this._accessToken = savedToken;
        this._refreshToken = savedRefresh;
        this.tokenExpiry = expiryTime;

        // Try to get profile
        try {
          this.profile = await this.getUserProfile(savedToken, { redirectOnUnauthorized });
          // Update Pinia store
          const authStore = useAuthStore();
          authStore.setAuth(true);
        } catch {
          // Profile fetch failed, but token might still be valid
        }

        return { access_token: savedToken };
      }
    }

    // Access token expired or missing - try to refresh
    if (savedRefresh) {
      // eslint-disable-next-line no-console
      console.log('[OAuth Debug] Access token expired, trying refresh');
      return await this.refreshAccessToken();
    }

    throw new Error('No valid token and no refresh token');
  }

  // Single-flight wrapper: concurrent callers (dashboard gate + checkout, twin
  // components) share one POST. With refresh-token rotation on the aether client,
  // parallel refreshes would consume each other's token and force a re-login.
  refreshAccessToken() {
    if (!this._refreshRequest) {
      this._refreshRequest = this._doRefreshAccessToken().finally(() => {
        this._refreshRequest = null;
      });
    }
    return this._refreshRequest;
  }

  // Refresh access token using refresh token
  async _doRefreshAccessToken() {
    const refreshToken = localStorage.getItem(`${localStorageKey}_refresh`);

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    const response = await fetch(`${baseUrl}/api/oauth/token-refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    if (!response.ok) {
      // Refresh token expired - clear tokens and throw
      // eslint-disable-next-line no-console
      console.log('[OAuth Debug] Refresh token expired or invalid');
      this.clearLocalStorage();
      throw new Error('Refresh token expired');
    }

    const tokenResponse = await response.json();
    // eslint-disable-next-line no-console
    console.log('[OAuth Debug] Refresh successful - new token stored');
    await this.localLogin(tokenResponse);
    return tokenResponse;
  }

  // Clear local storage
  clearLocalStorage() {
    localStorage.removeItem(localStorageKey);
    localStorage.removeItem(`${localStorageKey}_token`);
    localStorage.removeItem(`${localStorageKey}_expiry`);
    localStorage.removeItem(`${localStorageKey}_refresh`);
  }

  // Logout user with proper OIDC end session flow
  async logOut(options = {}) {
    // Capture the id_token_hint before clearing — performOIDCEndSession needs it.
    const currentIdToken = this.idToken;

    // Determine post-logout redirect URI
    const postLogoutRedirectUri =
      options.returnTo ||
      options.post_logout_redirect_uri ||
      `${window.location.protocol}//${window.location.host}/logout`;

    // Clear local auth state up front so the user is logged out immediately and
    // deterministically, regardless of whether the aether end-session round-trip
    // completes. The redirect below is best-effort server-side session cleanup;
    // handlePostLogoutCallback re-runs clearLocalState on return (idempotent).
    this.clearLocalState();

    // Perform proper OIDC end session if we have an ID token and OAuth server supports it
    if (currentIdToken && OAUTH_BASE_URL) {
      // Mark that logout is in progress
      sessionStorage.setItem('logout_in_progress', 'true');

      await this.performOIDCEndSession(currentIdToken, postLogoutRedirectUri);
    } else {
      // No OIDC session to end, just redirect
      window.location.href = postLogoutRedirectUri;
    }
  }

  // Clear local authentication state
  clearLocalState() {
    this.clearLocalStorage();

    this.idToken = null;
    this.tokenExpiry = null;
    this._accessToken = null;
    this._refreshToken = null;
    this.profile = null;

    this.emit(loginEvent, { loggedIn: false });
    // Update Pinia store
    const authStore = useAuthStore();
    authStore.setAuth(false);
  }

  // Perform OIDC end session with proper redirect
  async performOIDCEndSession(idToken, postLogoutRedirectUri) {
    // Build the OIDC end session URL according to OpenID Connect RP-Initiated Logout spec
    const endSessionUrl = new URL(`${OAUTH_BASE_URL}/api/oidc/end-session`);

    // Add required parameters for OIDC end session
    if (idToken) {
      endSessionUrl.searchParams.append('id_token_hint', idToken);
    }

    if (postLogoutRedirectUri) {
      endSessionUrl.searchParams.append('post_logout_redirect_uri', postLogoutRedirectUri);
    }

    // Add optional state parameter for security
    const logoutState = generateRandomState();
    endSessionUrl.searchParams.append('state', logoutState);

    // Store logout state for validation if needed
    sessionStorage.setItem('logout_state', logoutState);

    // Redirect to the OAuth server's end session endpoint
    window.location.href = endSessionUrl.toString();

    // set timeout to clear logout in progress state after redirect
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }

  // Alternative logout method that doesn't redirect (for API calls)
  logOutSilent() {
    this.clearLocalStorage();

    this.idToken = null;
    this.tokenExpiry = null;
    this._accessToken = null;
    this._refreshToken = null;
    this.profile = null;

    this.emit(loginEvent, { loggedIn: false });
    // Update Pinia store
    const authStore = useAuthStore();
    authStore.setAuth(false);

    // Optionally call logout endpoint without redirect
    if (OAUTH_BASE_URL) {
      const logoutUrl = `${OAUTH_BASE_URL}/api/oauth/logout`;

      // Use fetch for silent logout call
      fetch(logoutUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(this._accessToken && { Authorization: `Bearer ${this._accessToken}` })
        }
      }).catch(() => {
        // Ignore logout endpoint errors for silent logout
      });
    }
  }

  // Handle post-logout callback (optional, for validation)
  handlePostLogoutCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const state = urlParams.get('state');
    const storedLogoutState = sessionStorage.getItem('logout_state');
    const logoutInProgress = sessionStorage.getItem('logout_in_progress');

    // If logout was in progress, clear the state now
    if (logoutInProgress) {
      sessionStorage.removeItem('logout_in_progress');

      this.clearLocalState();
    }

    // Validate logout state if present
    if (state && storedLogoutState && state === storedLogoutState) {
      // Clear the logout state
      sessionStorage.removeItem('logout_state');

      // Emit logout completed event
      this.emit('logoutCompleted', { state });

      return true;
    } else if (state || storedLogoutState) {
      // State mismatch - potential security issue
      // Logout state mismatch detected
      return false;
    }

    // No state parameters - normal case
    return true;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return this.tokenExpiry && this.tokenExpiry > new Date() && localStorage.getItem(localStorageKey) === 'true';
  }

  // Get user ID from profile
  getUserId() {
    return (this.profile || {}).id || (this.profile || {}).sub;
  }

  // Get user email from profile
  getUserEmail() {
    return (this.profile || {}).email;
  }

  // Get username from profile
  getUsername() {
    return (this.profile || {}).username || (this.profile || {}).preferred_username;
  }

  // Get current access token
  getAccessToken() {
    if (this._accessToken) {
      return this._accessToken;
    }
    // Fall back to localStorage when silentAuth() hasn't completed yet
    const savedToken = localStorage.getItem(`${localStorageKey}_token`);
    const savedExpiry = localStorage.getItem(`${localStorageKey}_expiry`);
    if (savedToken && savedExpiry) {
      const expiryTime = new Date(parseInt(savedExpiry));
      if (expiryTime > new Date()) {
        this._accessToken = savedToken;
        this.tokenExpiry = expiryTime;
        return this._accessToken;
      }
    }
    return null;
  }

  // Backward compatibility - direct property access
  get accessToken() {
    return this._accessToken;
  }

  // Get current ID token
  getIdToken() {
    return this.idToken;
  }

  // Get user profile
  getProfile() {
    return this.profile;
  }

  // Add event listener (EventEmitter method)
  addListener(event, listener) {
    this.on(event, listener);
  }

  // Remove event listener (EventEmitter method)
  removeListener(event, listener) {
    this.off(event, listener);
  }
}

export default new CustomOAuthService();
